import { Component, Inject, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { EmailContents } from '@app/shared/constants';
import { ApplicationInsightEvent } from '@app/shared/enums';
import { DescriptiveNotificationComponent } from '@app/shared/notifications/descriptive-notification/descriptive-notification.component';
import { NotificationsService } from '@app/shared/notifications/notifications.service';
import { Email, EmailService } from '@app/shared/services/api/email.service';
import { ScenarioPlannerService } from '@app/shared/services/api/scenario-planner.service';
import { AppInsightsServiceDirective } from '@app/shared/services/app-insights.service';
import { AuthService } from '@app/shared/services/auth.service';
import { KamService } from '@app/shared/services/kam.service';
import { User } from '@app/shared/models/user';
import { UserMunicipalitiesService } from '@app/shared/services/user-municipalities.service';
import { isEmailValid } from '@app/shared/utility';
import { environment } from '@env/environment';
import { filter, isNil, split, trim } from 'lodash';
import { Observable, Subject, forkJoin, of } from 'rxjs';
import { first, switchMap, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-email-scenario-dialog',
  templateUrl: './email-scenario-dialog.component.html',
  styleUrls: ['./email-scenario-dialog.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class EmailScenarioDialogComponent implements OnInit, OnDestroy {
  protected unsubscribe$: Subject<void> = new Subject<void>();
  recipients = '';
  isSaved = false;
  isError = false;
  createdBy: string = '';
  municipality: string = '';
  hideClose = true;
  loading = false;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    public matDialogRef: MatDialogRef<EmailScenarioDialogComponent>,
    private appInsightsServiceDirective: AppInsightsServiceDirective,
    private authService: AuthService,
    private emailService: EmailService,
    private kamService: KamService,
    private notificationsService: NotificationsService,
    private scenarioPlannerService: ScenarioPlannerService,
    private userMunicipalitiesService: UserMunicipalitiesService
  ) {
    this.notificationsService.destroy();
  }

  public ngOnInit(): void {
    if (this.data.isSent) {
      this.reset();
    }

    if (!this.data.createdByName) {
      this.createdBy = `${this.authService.currentUser.firstname} ${this.authService.currentUser.lastname}`;
    } else {
      this.createdBy = this.data.createdByName;
    }

    this.municipality = this.data.municipalityName;

    if (this.authService.isAdminAreaAndMunicipalityWorker) {
      this.kamService
        .getByMunicipalityIdDirect(this.userMunicipalitiesService.currentMunicipality.guid)
        .pipe(
          switchMap((kamContact: User | null) => {
            if (isNil(kamContact)) {
              return of(null);
            }

            this.appInsightsServiceDirective.logEvent(ApplicationInsightEvent.streetlightPlannerSubmit, {
              scenarioType: this.data.scenarioType,
              budget: this.data.budget,
              municipalityName: this.data.municipalityName,
            });

            return this.scenarioPlannerService.submit(this.data.id);
          }),
          takeUntil(this.unsubscribe$)
        )
        .subscribe((res) => {
          if (!isNil(res)) {
            this.reset();
          } else {
            this.notificationsService.open(DescriptiveNotificationComponent, {
              model: {
                description: 'Für Ihre Gemeinde wurde kein primärer Key Account Manager gefunden.',
              },
            });
          }
        });
    } else {
      this.loading = false;
    }
  }

  public ngOnDestroy(): void {
    this.matDialogRef.close();
    this.notificationsService.destroy();
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  public send(recipients: string): void {
    const addresses = filter(split(recipients, ';'), (a) => {
      return trim(a).length > 0;
    });

    let invalidCount = 0;
    addresses.forEach((a) => {
      const email = trim(a);
      if (!isEmailValid(email)) {
        invalidCount++;
      }
    });

    if (invalidCount > 0 || addresses.length <= 0) {
      this.isError = true;
      return;
    }

    this.loading = true;
    this.isError = false;
    const emailArr: Observable<any>[] = [];
    addresses.forEach((address) => {
      const emailData = {
        Link: `${environment.energyPortalAppUrl}energiekarte/${this.data.id}`,
        Municipality: this.municipality,
        CreatedBy: this.createdBy,
      };

      const email = {
        subject: EmailContents.ScenarioPlannerCreated.subject,
        templateId: EmailContents.ScenarioPlannerCreated.templateId,
        recipient: address,
        data: JSON.stringify(emailData),
      } as Email;

      const email$ = this.emailService.sendEmail(email);
      emailArr.push(email$);
    });

    forkJoin(emailArr)
      .pipe(
        switchMap(() => this.scenarioPlannerService.submit(this.data.id)),
        first()
      )
      .subscribe(() => {
        this.reset();
      });

    this.appInsightsServiceDirective.logEvent(ApplicationInsightEvent.streetlightPlannerSubmit, {
      scenarioType: this.data.scenarioType,
      budget: this.data.budget,
      municipalityName: this.data.municipalityName,
    });
  }

  private reset(): void {
    this.isSaved = true;
    this.loading = false;
    this.hideModal(3000);
  }

  private hideModal(time: number): void {
    setTimeout(() => {
      this.matDialogRef.close();
    }, time);
  }
}
